import { Link } from 'react-router-dom'
import { ArrowUpRight, Phone } from 'lucide-react'
import { SITE, whatsappHref } from '../../data/site'
import { useGsapContext } from '../../hooks/useGsapReveal'
import {
  magnetize,
  revealOnScroll,
  riseIn,
  staggerChildren,
} from '../../animations/gsapAnimations'
import BrandMark from '../ui/BrandMark'
import WhatsAppIcon from '../ui/WhatsAppIcon'

const SOCIALS = [
  {
    name: 'Instagram',
    href: 'https://instagram.com/sultanmotors28',
    path: 'M12 2.2c3.2 0 3.6 0 4.9.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.86s0 3.6-.07 4.86c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.86.07s-3.6 0-4.86-.07c-1.17-.05-1.8-.25-2.23-.41a3.8 3.8 0 0 1-1.38-.9 3.8 3.8 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.2 15.6 2.2 15.2 2.2 12s0-3.6.07-4.86c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.46 2.2 8.84 2.2 12 2.2Zm0 1.8c-3.15 0-3.5.01-4.74.07-1.14.05-1.76.24-2.17.4-.55.21-.94.47-1.35.88-.41.41-.67.8-.88 1.35-.16.41-.35 1.03-.4 2.17-.06 1.24-.07 1.6-.07 4.74s.01 3.5.07 4.74c.05 1.14.24 1.76.4 2.17.21.55.47.94.88 1.35.41.41.8.67 1.35.88.41.16 1.03.35 2.17.4 1.24.06 1.6.07 4.74.07s3.5-.01 4.74-.07c1.14-.05 1.76-.24 2.17-.4.55-.21.94-.47 1.35-.88.41-.41.67-.8.88-1.35.16-.41.35-1.03.4-2.17.06-1.24.07-1.6.07-4.74s-.01-3.5-.07-4.74c-.05-1.14-.24-1.76-.4-2.17a3.6 3.6 0 0 0-.88-1.35 3.6 3.6 0 0 0-1.35-.88c-.41-.16-1.03-.35-2.17-.4C15.5 4.01 15.15 4 12 4Zm0 3.06a4.94 4.94 0 1 1 0 9.88 4.94 4.94 0 0 1 0-9.88Zm0 1.8a3.14 3.14 0 1 0 0 6.28 3.14 3.14 0 0 0 0-6.28Zm5.14-3.2a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3Z',
  },
  {
    name: 'Facebook',
    href: 'https://facebook.com/sultanmotors28',
    path: 'M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.52 1.5-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.9h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94Z',
  },
  {
    name: 'WhatsApp',
    href: whatsappHref(),
    path: 'M12 2a10 10 0 0 0-8.6 15.05L2 22l5.08-1.33A10 10 0 1 0 12 2Zm0 1.9a8.1 8.1 0 0 1 6.6 12.78 8.1 8.1 0 0 1-10.9 2.1l-.36-.22-3.02.79.8-2.94-.23-.37A8.1 8.1 0 0 1 12 3.9Zm-3.6 4c-.18 0-.47.07-.72.34-.25.27-.95.93-.95 2.26s.98 2.62 1.11 2.8c.14.18 1.9 2.9 4.6 4.06 2.25.97 2.71.78 3.2.73.49-.04 1.57-.64 1.79-1.26.22-.62.22-1.15.16-1.26-.07-.11-.25-.18-.52-.31-.27-.14-1.57-.78-1.82-.87-.24-.09-.42-.13-.6.14-.17.26-.68.86-.83 1.04-.16.18-.31.2-.58.07-.27-.14-1.12-.42-2.14-1.32-.79-.7-1.32-1.57-1.48-1.84-.15-.27-.01-.41.12-.55.12-.12.27-.31.4-.47.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.47-.07-.14-.6-1.45-.82-1.98-.21-.51-.43-.44-.6-.45h-.5Z',
  },
] as const

/* The brief's four columns. Privacy and Terms join Support once those pages
   exist — a footer link to a 404 is worse than no link. */
const COLUMNS = [
  {
    title: 'Company',
    links: [
      { label: 'About us', to: '/about' },
      { label: 'Our showroom', to: '/contact' },
      { label: 'Contact', to: '/contact' },
    ],
  },
  {
    title: 'Vehicles',
    links: [
      { label: 'All cars', to: '/home#cars' },
      { label: 'New arrivals', to: '/home?sort=newest#cars' },
      { label: 'Featured cars', to: '/home?sort=featured#cars' },
    ],
  },
  {
    title: 'Financing',
    links: [
      { label: 'How it works', to: '/financing' },
      { label: 'Lease calculator', to: '/financing' },
      { label: 'Apply for financing', to: '/financing' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Customer policy', to: '/policy' },
      { label: 'FAQ', to: '/financing#faq' },
      { label: 'Book a test drive', to: '/contact' },
      { label: 'Request a callback', to: '/contact' },
    ],
  },
] as const

function Footer() {
  /* Four movements, in the order they are read: the closing card arrives as
     one piece with the ghost ring climbing up behind its headline, then the
     link columns, then the showroom details, and last the marque rising out
     of the floor as the page runs out. The ring and the marque are scrubbed
     — architecture moves with the visitor, not on a clock. */
  const ref = useGsapContext<HTMLElement>((scope) => {
    const cta = scope.querySelector('[data-footer="cta"]')
    if (cta) revealOnScroll(cta, { y: 34 })

    const ring = scope.querySelector('[data-footer="ring"]')
    if (ring && cta) {
      riseIn(ring, { amount: 40, trigger: cta, start: 'top bottom', end: 'center center' })
    }

    const marque = scope.querySelector('[data-footer="marque"]')
    if (marque) riseIn(marque, { amount: 70, start: 'top bottom', end: 'bottom bottom-=24' })

    const columns = scope.querySelector('[data-footer="columns"]')
    if (columns) staggerChildren(columns, ':scope > *', { y: 22, stagger: 0.07 })

    const details = scope.querySelector('[data-footer="details"]')
    if (details) staggerChildren(details, ':scope > *', { y: 18, stagger: 0.08 })

    return magnetize(scope)
  })

  return (
    <footer
      ref={ref}
      className="relative overflow-hidden border-t border-line-soft band-footer pb-[72px] md:pb-0"
    >
      {/* ---------- closing CTA ----------
          One card, lit from within: the lime falls in from the top corner
          the way the hero's ring lights the car, and the two actions sit on
          it the way they sit in the hero — one filled, one outlined. */}
      <section
        aria-labelledby="footer-cta-title"
        className="mx-auto max-w-[1320px] px-5 pt-20 sm:px-8 sm:pt-24"
      >
        <div
          data-footer="cta"
          className="relative overflow-hidden rounded-3xl border border-lime/25 bg-raised px-7 py-14 text-center shadow-pop sm:px-14 sm:py-20"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(680px_320px_at_80%_-24%,rgb(200_245_66/0.22),transparent),radial-gradient(520px_280px_at_0%_115%,rgb(200_245_66/0.12),transparent)]"
          />
          {/* A ghost of the hero's ring, rising behind the headline. */}
          <span
            aria-hidden="true"
            data-footer="ring"
            className="pointer-events-none absolute left-1/2 -bottom-[260px] size-[520px] -translate-x-1/2 rounded-full border-[3px] border-lime/25 [box-shadow:0_0_60px_rgb(200_245_66/0.18)]"
          />

          <p className="relative flex items-center justify-center gap-3 label text-lime">
            <span aria-hidden="true" className="h-px w-9 coachline" />
            {SITE.city} · Open 7 days
            <span aria-hidden="true" className="h-px w-9 coachline" />
          </p>
          <h2
            id="footer-cta-title"
            className="relative mx-auto mt-6 max-w-[18ch] font-display text-[clamp(2rem,4.6vw,3.6rem)] leading-[1.04] font-bold tracking-[-0.03em] text-platinum"
          >
            Let&rsquo;s get you <span className="text-lime">approved</span>
          </h2>
          <p className="relative mx-auto mt-5 max-w-[52ch] text-[15.5px] leading-relaxed text-steel">
            Send us the car you want. We come back with every bank&rsquo;s
            number in one message — usually the same day.
          </p>

          <div className="relative mt-10 flex flex-wrap items-center justify-center gap-3">
            <a
              href={SITE.phoneHref}
              data-magnetic
              className="group/btn inline-flex h-14 items-center gap-3 btn-sheen btn-lime rounded-full pl-7 pr-2 text-[14.5px] font-semibold tracking-[-0.01em] text-void shadow-action transition duration-300"
            >
              {SITE.phone}
              <span
                aria-hidden="true"
                className="grid size-10 place-items-center rounded-full bg-void text-lime"
              >
                <Phone strokeWidth={2} className="size-4" />
              </span>
            </a>
            <a
              href={whatsappHref()}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-14 items-center gap-3 rounded-full border border-lime/55 pl-7 pr-2 text-[14.5px] font-semibold tracking-[-0.01em] text-platinum transition duration-300 hover:border-lime hover:bg-lime/10"
            >
              Message on WhatsApp
              <span
                aria-hidden="true"
                className="grid size-10 place-items-center rounded-full border border-lime text-lime"
              >
                <WhatsAppIcon className="size-4" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ---------- link columns ---------- */}
      <div className="relative mx-auto max-w-[1320px] px-5 pt-20 sm:px-8">
        <div
          data-footer="columns"
          className="grid grid-cols-2 gap-x-8 gap-y-12 border-b border-line-soft pb-14 lg:grid-cols-[1.5fr_repeat(4,1fr)]"
        >
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="block size-12 shrink-0">
                <BrandMark className="size-full rounded-full object-cover" />
              </span>
              <span className="flex flex-col leading-none">
                <strong className="font-display text-[19px] font-bold tracking-[-0.03em] text-platinum">
                  {SITE.name}
                </strong>
                <small className="mt-1 text-[9.5px] font-semibold tracking-[0.28em] text-lime/85 uppercase">
                  {SITE.tagline}
                </small>
              </span>
            </div>

            <p className="mt-6 max-w-[38ch] text-[14px] leading-relaxed text-steel">
              Brand new cars on bank lease, sourced and delivered across
              Islamabad and Rawalpindi. One file, every bank, no fine print.
            </p>

            <ul className="mt-7 flex gap-2.5">
              {SOCIALS.map((social) => (
                <li key={social.name}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.name}
                    className="icon-ring size-11 text-steel transition duration-300 hover:bg-lime hover:text-void"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      className="size-4.5"
                    >
                      <path d={social.path} />
                    </svg>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {COLUMNS.map((column) => (
            <nav key={column.title} aria-label={column.title}>
              <h3 className="label-sm text-lime">{column.title}</h3>
              <ul className="mt-5 flex flex-col gap-3.5">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="group/foot inline-flex items-center gap-1.5 text-[14px] text-steel transition-colors duration-200 hover:text-platinum"
                    >
                      {link.label}
                      <ArrowUpRight
                        aria-hidden="true"
                        strokeWidth={2}
                        className="size-3 -translate-x-1 text-lime opacity-0 transition duration-200 group-hover/foot:translate-x-0 group-hover/foot:opacity-100"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* ---------- visit / hours ---------- */}
        <dl data-footer="details" className="grid grid-cols-1 gap-8 py-10 sm:grid-cols-3">
          {[
            { term: 'Showroom', value: SITE.address },
            { term: 'Hours', value: SITE.hours },
            { term: 'Email', value: SITE.email },
          ].map((item) => (
            <div key={item.term}>
              <dt className="label-sm text-dim">{item.term}</dt>
              <dd className="mt-2 text-[14.5px] text-silver">{item.value}</dd>
            </div>
          ))}
        </dl>

        {/* ---------- the marque, embossed across the foot ---------- */}
        <p
          aria-hidden="true"
          className="pointer-events-none -mb-[1.5vw] overflow-hidden text-center font-display text-[clamp(3rem,9.4vw,8.6rem)] leading-none font-extrabold tracking-[-0.03em] whitespace-nowrap text-ghost select-none"
        >
          <span data-footer="marque" className="inline-block">
            Sultan Motors {SITE.suffix}
          </span>
        </p>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-line-soft py-8 sm:flex-row">
          <p className="text-[11.5px] text-dim">
            © {new Date().getFullYear()} {SITE.name} {SITE.suffix}. All rights
            reserved.
          </p>
          <p className="text-[11.5px] text-dim">
            Instalments shown are indicative and subject to bank approval.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
